'use client';

import { c as _c } from "react/compiler-runtime";
import { SectionLabel } from '@/components/ui/section-label';
import { segmentClassName, segmentedTrackClassName } from '@/components/ui/segmented-control';
import { SkeletonBar } from '@/features/payload-cms/components/search/search-skeleton';
import { periodLabel, periodLabels, sortLabel, sortLabels, typeFilterLabel, typeLabels } from '@/features/payload-cms/components/search/search-translations';
import type { SearchPeriod, SearchResultType, SearchSort } from '@/features/payload-cms/search/search-types';
import { SEARCH_PERIODS, SEARCH_RESULT_TYPES, SEARCH_SORTS } from '@/features/payload-cms/search/search-types';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { Check } from 'lucide-react';
import type React from 'react';
export interface SearchFilters {
  types: SearchResultType[];
  sort: SearchSort;
  period: SearchPeriod;
}

/** A small square chip, filled brand green while chosen. */
export const filterChipClassName = (isChosen: boolean): string => cn('inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-[14px] font-semibold whitespace-nowrap transition-colors lg:h-9', 'disabled:cursor-default disabled:opacity-45', isChosen ? 'bg-conveniat-green text-white' : 'bg-chip text-green-900 hover:bg-tint-line');
const periodText = (period: SearchPeriod, locale: Locale): string => period === 'year' ? new Intl.DateTimeFormat('en', {
  year: 'numeric',
  timeZone: 'Europe/Zurich'
}).format(new Date()) : periodLabels[period][locale];

/**
 * The filters of the search: which types, in which order, and how recent. The same panel sits
 * beside the results on a wide screen and in the bottom sheet on a phone.
 */
export const SearchFilterPanel: React.FC<{
  filters: SearchFilters;
  /** undefined while the counts of the current query are on their way */
  counts: Record<SearchResultType, number> | undefined;
  locale: Locale;
  onChange: (filters: Partial<SearchFilters>) => void;
}> = t0 => {
  const $ = _c(36);
  const {
    filters,
    counts,
    locale,
    onChange
  } = t0;
  let t1;
  if ($[0] !== filters || $[1] !== onChange) {
    t1 = type => {
      const types = filters.types.includes(type) ? filters.types.filter(chosen => chosen !== type) : [...filters.types, type];
      onChange({
        types: types.length === SEARCH_RESULT_TYPES.length ? [] : types
      });
    };
    $[0] = filters;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const toggleType = t1;
  const t2 = typeFilterLabel[locale];
  let t3;
  if ($[3] !== t2) {
    t3 = <SectionLabel as="h3">{t2}</SectionLabel>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== counts || $[6] !== filters || $[7] !== locale || $[8] !== toggleType) {
    t4 = SEARCH_RESULT_TYPES.map(type_0 => {
      const isChecked = filters.types.includes(type_0);
      const isEmpty = counts?.[type_0] === 0 && !isChecked;
      return <li key={type_0}><label className={cn("text-heading flex min-h-11 cursor-pointer items-center gap-3 text-[15px] font-semibold", isEmpty && "cursor-default opacity-45")}><input type="checkbox" className="peer sr-only" checked={isChecked} disabled={isEmpty} onChange={() => toggleType(type_0)} /><span aria-hidden="true" className={cn("peer-focus-visible:ring-conveniat-green flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border-2 peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2", isChecked ? "border-conveniat-green bg-conveniat-green text-white" : "border-rail bg-white")}>{isChecked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}</span><span className="flex-1">{typeLabels[type_0][locale]}</span>{counts === undefined ? <SkeletonBar className="h-3.5 w-4" /> : <span className="text-[14px] font-medium text-green-500">{counts[type_0]}</span>}</label></li>;
    });
    $[5] = counts;
    $[6] = filters;
    $[7] = locale;
    $[8] = toggleType;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <ul className="divide-line mt-2 flex flex-col divide-y">{t4}</ul>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = sortLabel[locale];
  let t7;
  if ($[12] !== t6) {
    t7 = <SectionLabel as="h3" className="mt-6">{t6}</SectionLabel>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn(segmentedTrackClassName, "mt-2 w-fit");
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== filters || $[16] !== locale || $[17] !== onChange) {
    t9 = SEARCH_SORTS.map(sort => <button key={sort} type="button" aria-pressed={filters.sort === sort} onClick={() => onChange({
      sort
    })} className={cn(segmentClassName(filters.sort === sort), "px-4 text-[14px] font-semibold")}>{sortLabels[sort][locale]}</button>);
    $[15] = filters;
    $[16] = locale;
    $[17] = onChange;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = periodLabel[locale];
  let t12;
  if ($[21] !== t11) {
    t12 = <SectionLabel as="h3" className="mt-6">{t11}</SectionLabel>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== filters || $[24] !== locale || $[25] !== onChange) {
    t13 = SEARCH_PERIODS.map(period => <button key={period} type="button" aria-pressed={filters.period === period} onClick={() => onChange({
      period
    })} className={filterChipClassName(filters.period === period)}>{periodText(period, locale)}</button>);
    $[23] = filters;
    $[24] = locale;
    $[25] = onChange;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <div className="mt-2 flex flex-wrap gap-2">{t13}</div>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t14 || $[32] !== t3 || $[33] !== t5 || $[34] !== t7) {
    t15 = <div className="flex flex-col">{t3}{t5}{t7}{t10}{t12}{t14}</div>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t14;
    $[32] = t3;
    $[33] = t5;
    $[34] = t7;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};